import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Button, Toggle } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import useEnhancedModelCompatibility from 'Pods/ThreeDStudio/Hooks/useEnhancedModelCompatibility'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import AIEngineSelector from 'Pods/ThreeDStudio/Shared/AIEngineSelector/AIEngineSelector'
import GenerateArea from 'Pods/ThreeDStudio/Shared/GenerateArea/GenerateArea'
import Placeholder from 'Pods/ThreeDStudio/Shared/Placeholder/Placeholder'
import { getHiddenEnhancedProviders } from 'Pods/ThreeDStudio/Shared/Utils/getHiddenEnhancedProviders'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setViewerMeshList } from 'Pods/ThreeDStudio/Store/actions/viewer'
import { ACTION } from 'Pods/ThreeDStudio/Store/constants'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import Preview from './Components/Preview/Preview'
import SegmentList from './Components/SegmentList/SegmentList'
import UnavailableModels from './Components/UnavailableModels/UnavailableModels'

// A part counts as "moved" when its gizmo transform offset is non-zero on any
// position or rotation axis (exploded view does not touch transformOffset).
const isMeshMoved = ({ transformOffset }) => {
  if (!transformOffset) {
    return false
  }
  const { position, rotation } = transformOffset
  return Boolean(
    (position && (position.x || position.y || position.z))
    || (rotation && (rotation.x || rotation.y || rotation.z)),
  )
}

function SegmentTab() {
  const t = useTranslation()
  const { segmentationFlowEnabled, enableHunyuanSegmentation } = useContext(ThreeDStudioPageContext)
  const { isModelIncompatible, tooltipMessage } = useEnhancedModelCompatibility()
  const hiddenProviders = useMemo(
    () => getHiddenEnhancedProviders(ACTION.MESH_SEGMENTATION, { enableHunyuanSegmentation }),
    [enableHunyuanSegmentation]
  )
  const meshList = useThreeDStudioStore(state => state.viewer.meshList)
  const isRigged = useThreeDStudioStore(state => state.viewer.isRigged)
  const explodedViewEnabled = useThreeDStudioStore(state => state.viewer.explodedViewEnabled)
  const segmentationRenderModeAvailable = useThreeDStudioStore(
    state => state.viewer.segmentationRenderModeAvailable
  )
  const selectedMeshId = useThreeDStudioStore(state => state.viewer.selectedMeshId)
  const viewer = useThreeDStudioStore(state => state.staticViewer.instance)

  const [isExplodeAnimating, setIsExplodeAnimating] = useState(false)
  const explodeAnimationTimerRef = useRef(null)
  const resetFrameRef = useRef(null)

  // Clear the in-flight animation timer and reset frame if the tab unmounts.
  useEffect(() => () => {
    clearTimeout(explodeAnimationTimerRef.current)
    cancelAnimationFrame(resetFrameRef.current)
  }, [])

  const handleToggleExploded = useCallback(() => {
    if (!viewer || isExplodeAnimating) {
      return
    }

    const nextEnabled = !explodedViewEnabled
    // Enabling the exploded view only reads as segments when the model is
    // rendered by segment color, so switch into the segmentation render mode.
    if (nextEnabled) {
      viewer.setRenderModeSettings({ mode: 'segmentation' })
    }
    viewer.setExplodedViewEnabled(nextEnabled)

    // Lock the toggle while the explode/implode animation plays out
    // (duration is in seconds) so it cannot be re-triggered mid-animation.
    const durationSeconds = viewer.getExplodedViewSettings?.().duration ?? 0
    if (durationSeconds > 0) {
      setIsExplodeAnimating(true)
      clearTimeout(explodeAnimationTimerRef.current)
      explodeAnimationTimerRef.current = setTimeout(() => {
        setIsExplodeAnimating(false)
      }, durationSeconds * 1000)
    }
  }, [viewer, explodedViewEnabled, isExplodeAnimating])

  const handleToggleVisibility = useCallback((uuid, nextVisible) => {
    viewer?.setMeshVisibility(uuid, nextVisible)
  }, [viewer])

  const resetMeshes = useCallback(() => {
    if (!viewer) {
      return
    }

    meshList.forEach((mesh) => {
      if (mesh.visible === false) {
        viewer.setMeshVisibility(mesh.uuid, true)
      }
      if (isMeshMoved(mesh)) {
        viewer.resetTransform(mesh.uuid)
      }
    })
    viewer.selectMeshById?.(null)
    // resetTransform mutates the mesh offsets in place without emitting a
    // meshList update, so push a fresh snapshot into the store. Otherwise a
    // move-only reset leaves the mirror stale and the button enabled.
    setViewerMeshList(viewer.getMeshList?.() ?? [])
  }, [viewer, meshList])

  // Reverts every change made in this tab: show all hidden parts, move any
  // repositioned parts back, collapse the exploded view, and clear the selection.
  const handleReset = useCallback(() => {
    if (!viewer) {
      return
    }

    // Collapsing the exploded view restores parts to the world positions they
    // held when it was enabled, which already include any gizmo moves. Reset
    // the transforms only after that restore happens, otherwise the collapse
    // re-applies the moved positions and the parts never return home. Force the
    // collapse to be instant (duration 0) so the reset does not play out the
    // implode animation, then reset on the next frame once the viewer's render
    // loop has restored the positions, and put the configured duration back.
    if (explodedViewEnabled) {
      const { duration } = viewer.getExplodedViewSettings?.() ?? {}
      viewer.setExplodedViewSettings?.({ duration: 0 })
      viewer.setExplodedViewEnabled(false)
      cancelAnimationFrame(resetFrameRef.current)
      resetFrameRef.current = requestAnimationFrame(() => {
        resetMeshes()
        if (typeof duration === 'number') {
          viewer.setExplodedViewSettings?.({ duration })
        }
      })
      return
    }

    resetMeshes()
  }, [viewer, explodedViewEnabled, resetMeshes])

  const hasSegments = meshList.length > 0 && segmentationRenderModeAvailable
  const hasSegmentChanges = explodedViewEnabled
    || meshList.some(mesh => mesh.visible === false || isMeshMoved(mesh))
  const isGenerateDisabled = isModelIncompatible || isRigged
  const generateTooltip = isRigged ? t('viewerSidebar.enhance.segment.riggedTooltip') : tooltipMessage

  if (!segmentationFlowEnabled) {
    return (
      <Placeholder
        title={t('viewerSidebar.enhance.segment.placeholderTitle')}
        description={t('viewerSidebar.enhance.segment.placeholderDescription')}
      >
        <Preview />
      </Placeholder>
    )
  }

  return (
    <>
      <div className="enhance-model-panel__tab segment-tab">
        <span className="enhance-model-panel__title cgt-heading-50">{t('viewerSidebar.enhance.segment.title')}</span>
        {hasSegments ? (
          <>
            <Toggle
              id="segment-exploded-view"
              isChecked={explodedViewEnabled}
              onChange={handleToggleExploded}
              disabled={isExplodeAnimating}
              label={t('viewerSidebar.enhance.segment.explodedView')}
              labelClassName="segment-tab__explode-label cgt-body-base-semibold"
            />
            <Button
              type="primary"
              size="small"
              customClass="segment-tab__reset"
              disabled={!hasSegmentChanges || isExplodeAnimating}
              onClick={handleReset}
            >
              {t('viewerSidebar.enhance.segment.resetModel')}
            </Button>
            <div className="segment-tab__part-list">
              <span className="segment-tab__part-list-title cgt-body-base-semibold">
                {t('viewerSidebar.enhance.segment.partList')}
              </span>
              <SegmentList
                meshes={meshList}
                selectedMeshId={selectedMeshId}
                onToggleVisibility={handleToggleVisibility}
              />
            </div>
          </>
        ) : (
          <>
            <div className="enhance-model-panel__settings">
              <AIEngineSelector isEnhanced hiddenProviders={hiddenProviders} />
            </div>
            <div className="segment-tab__flow">
              <Preview />
              <UnavailableModels />
            </div>
          </>
        )}
      </div>
      {!hasSegments && (
        <GenerateArea
          generateLabel={t('viewerSidebar.enhance.segment.generateLabel')}
          generatedLabel={t('viewerSidebar.enhance.common.done')}
          generatingLabel={t('viewerSidebar.enhance.segment.generatingLabel')}
          isEnhancementMode
          isGenerateDisabled={isGenerateDisabled}
          tooltipMessage={generateTooltip}
          withIcon={false}
        />
      )}
    </>
  )
}

export default memo(SegmentTab)
